import { apiBaseUrl } from './client';
import { apiFetch, clearSignedOut } from './http';

/**
 * Everything the console asks the control plane, and who it is when it asks.
 *
 * The session is a cookie the control plane sets, which is why every request has to opt in with
 * `credentials: 'include'` — a browser sends none otherwise, and the request would arrive looking
 * like a stranger's. That is the whole reason this wrapper exists: there were twenty-odd separate
 * `fetch` calls and no single place to say it once.
 */

export type ConsoleRole = 'admin' | 'dev';

export interface SignedInUser {
  id: string;
  email: string;
  name: string;
  role: ConsoleRole;
  mustChangePassword: boolean;
  disabled: boolean;
  createdAt: string;
  lastSeenAt: string | null;
}

const detailOf = async (response: Response, fallback: string): Promise<string> => {
  try {
    const problem = (await response.json()) as { detail?: string; title?: string };

    return problem.detail ?? problem.title ?? fallback;
  } catch {
    return fallback;
  }
};

const post = async <T>(path: string, body: unknown, fallback: string): Promise<T> => {
  const response = await apiFetch(`${apiBaseUrl()}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  if (!response.ok) throw new Error(await detailOf(response, fallback));

  return (await response.json()) as T;
};

export const signIn = async (email: string, password: string): Promise<SignedInUser> => {
  const { user } = await post<{ user: SignedInUser }>(
    '/api/v1/auth/login',
    { email, password },
    'That email and password do not match an account.',
  );
  clearSignedOut();

  return user;
};

export const signOut = async (): Promise<void> => {
  await apiFetch(`${apiBaseUrl()}/api/v1/auth/logout`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: '{}',
  });
};

/** Who is signed in, or nothing. A 401 here is the ordinary answer, not a failure. */
export const whoAmI = async (): Promise<SignedInUser | undefined> => {
  const response = await fetch(`${apiBaseUrl()}/api/v1/auth/me`, { credentials: 'include' });
  if (!response.ok) return undefined;

  return ((await response.json()) as { user: SignedInUser }).user;
};

export const changeOwnPassword = (currentPassword: string, newPassword: string): Promise<unknown> =>
  post('/api/v1/auth/password', { currentPassword, newPassword }, 'That password was not accepted.');

export const listUsers = async (): Promise<SignedInUser[]> => {
  const response = await apiFetch(`${apiBaseUrl()}/api/v1/users`);
  if (!response.ok) throw new Error(await detailOf(response, 'Could not read the list of users.'));

  return ((await response.json()) as { users: SignedInUser[] }).users;
};

export const createUser = (input: {
  email: string;
  name: string;
  role: ConsoleRole;
}): Promise<{ user: SignedInUser; password: string }> =>
  post('/api/v1/users', input, 'Could not create that user.');

export const resetUserPassword = (id: string): Promise<{ password: string }> =>
  post(`/api/v1/users/${encodeURIComponent(id)}/password`, {}, 'Could not reset that password.');

export const setUserDisabled = (id: string, disabled: boolean): Promise<{ user: SignedInUser }> =>
  post(
    `/api/v1/users/${encodeURIComponent(id)}/${disabled ? 'disable' : 'enable'}`,
    {},
    'Could not change that account.',
  );
